import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Table.css";
import "./TicketDetailV2.css";
import {
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SendIcon from "@mui/icons-material/Send";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LockIcon from "@mui/icons-material/Lock";
import ReplayIcon from "@mui/icons-material/Replay";
import AddIcon from "@mui/icons-material/Add";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  withMandatoryDepartments,
  mandatoryDeptIdSet,
} from "../utils/mandatoryAssignees";

// This page mounts its own id-scoped ToastContainer. In react-toastify v11,
// when every mounted container has a containerId, a toast() call without a
// matching containerId renders in no container at all. Every toast.* below
// must pass this id, or the message silently vanishes (e.g. the 403 shown
// when a non-Planning user tries to close a ticket).
const TOAST_CONTAINER_ID = "ticket-detail-v2";
const toastOpts = { containerId: TOAST_CONTAINER_ID };

const STATUS_OPTIONS = [
  "Open",
  "Assigned",
  "In Progress",
  "Resolved",
  "Closed",
  "Reopened",
];

const PRIORITY_TONES = {
  Low: { color: "#15803d", dot: "#22c55e", bg: "#f0fdf4" },
  Medium: { color: "#b45309", dot: "#f59e0b", bg: "#fffbeb" },
  High: { color: "#c2410c", dot: "#f97316", bg: "#fff7ed" },
  Critical: { color: "#b91c1c", dot: "#dc2626", bg: "#fef2f2" },
};

const STATUS_TONES = {
  Open: { color: "#15803d", dot: "#22c55e", bg: "#ecfdf5" },
  Assigned: { color: "#1d4ed8", dot: "#3b82f6", bg: "#eff6ff" },
  "In Progress": { color: "#6d28d9", dot: "#7c3aed", bg: "#f5f3ff" },
  Resolved: { color: "#047857", dot: "#059669", bg: "#ecfdf5" },
  Closed: { color: "#4b5563", dot: "#6b7280", bg: "#f3f4f6" },
  Reopened: { color: "#b91c1c", dot: "#dc2626", bg: "#fef2f2" },
};

const TYPE_TONES = {
  Alert: { color: "#c2410c", dot: "#f97316", bg: "#fff7ed" },
  "Missing Information": { color: "#1d4ed8", dot: "#3b82f6", bg: "#eff6ff" },
};

// Type badges carry a glyph, not just a dot, so the ticket's nature reads instantly.
const TYPE_ICONS = {
  Alert: WarningAmberIcon,
  "Missing Information": InfoOutlinedIcon,
};

const DEFAULT_TONE = { color: "#374151", dot: "#94a3b8", bg: "#f3f4f6" };

// Each status gets a glyph so it reads at a glance, not just by colour.
// Kept in sync with the ticket list (TicketsV2.js).
const STATUS_ICONS = {
  Open: RadioButtonUncheckedIcon,
  Assigned: AssignmentIndIcon,
  "In Progress": AutorenewIcon,
  Resolved: TaskAltIcon,
  Closed: LockIcon,
  Reopened: ReplayIcon,
};

// Icon + label, tinted with the status colour. Used in the status dropdown
// (menu rows and the selected value).
const StatusLabel = ({ status }) => {
  const Icon = STATUS_ICONS[status];
  const tone = STATUS_TONES[status] || DEFAULT_TONE;
  return (
    <span className="ticket-detail-status-option">
      {Icon && <Icon sx={{ fontSize: 16, color: tone.dot || tone.color }} />}
      {status}
    </span>
  );
};

const AVATAR_PALETTE = [
  "#991b1b",
  "#0f766e",
  "#7c3aed",
  "#b45309",
  "#1d4ed8",
  "#be185d",
  "#15803d",
];

const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const colorForName = (name) => {
  const str = String(name || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.round(diffDays / 30)}mo ago`;
};

// "3d ago · 04 Jul 2026, 09:40 AM" — relative for quick scanning, absolute for precision.
const formatRelativeAbsolute = (isoString) => {
  if (!isoString) return "-";
  return `${formatRelativeTime(isoString)} · ${formatDateTime(isoString)}`;
};

const isImageFile = (filename) => /\.(jpe?g|png|gif|webp)$/i.test(filename || "");

// Mentions live in the comment body as @[Display Name](user-id) tokens — the
// same shape the backend parses to resolve who to notify. These helpers detect
// an in-progress mention while typing and escape names for safe replacement.
const MENTION_TOKEN = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/g;

// From the text up to the caret, find an in-progress "@query" (the last @ with
// no whitespace after it, sitting at a word start) → { start, query }, or null.
const detectMentionTrigger = (value, caret) => {
  const upto = value.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(value[at - 1])) return null; // must follow start or space
  const query = upto.slice(at + 1);
  if (/\s/.test(query) || query.length > 40) return null;
  return { start: at, query };
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// A stable fingerprint of the meaningful ticket state, excluding the volatile
// presigned attachment URLs (which change on every fetch). A live refresh only
// re-renders when this differs — so an unchanged refetch causes no image reload.
const ticketSignature = (t) => {
  if (!t) return "";
  return JSON.stringify({
    status: t.status,
    title: t.title,
    description: t.description,
    updated_at: t.updated_at,
    assignees: {
      u: (t.assignees?.users || []).map((x) => x.id).sort(),
      d: (t.assignees?.departments || []).map((x) => x.id).sort(),
    },
    attachments: (t.attachments || []).map((a) => a.file_id).sort(),
    activity: (t.activity || []).map((a) => ({
      t: a.type,
      c: a.comment_id,
      b: a.body,
      s: a.to_status,
      f: a.from_status,
      at: a.at,
      e: a.is_edited,
      att: (a.attachments || []).map((x) => x.file_id).sort(),
    })),
  });
};

// file_size is stored in KB (a string) by the uploader. Scale it to B/KB/MB so
// large files don't read as "3072 KB" and sub-KB files don't read as "0 KB".
const formatFileSize = (sizeKb) => {
  const kb = Number(sizeKb);
  if (!Number.isFinite(kb) || kb <= 0) return "";
  if (kb < 1) return `${Math.round(kb * 1024)} B`;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

// "PDF · 248 KB" — file type from the extension plus the size, when known.
const fileMetaLabel = (file) => {
  const ext = String(file?.filename || "").split(".").pop();
  const type = ext && ext !== file?.filename ? ext.toUpperCase() : "FILE";
  const size = formatFileSize(file?.file_size);
  return size ? `${type} · ${size}` : type;
};

// Assignees can arrive duplicated (e.g. a department listed twice); render each once.
const uniqueById = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    if (item == null || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

// Header pill badge. A tinted pill with either a leading glyph (types) or a
// colour dot (priority); the ticket number uses the neutral monospace variant.
const Badge = ({ tone, label, icon: Icon, mono }) => {
  if (mono) {
    return <span className="ticket-detail-badge ticket-detail-badge-mono">{label || "-"}</span>;
  }
  const t = tone || DEFAULT_TONE;
  return (
    <span className="ticket-detail-badge" style={{ color: t.color, background: t.bg }}>
      {Icon ? (
        <Icon sx={{ fontSize: 13 }} />
      ) : (
        <span className="ticket-detail-badge-dot" style={{ background: t.dot }} />
      )}
      {label || "-"}
    </span>
  );
};

// Small colored pill for a status value inside an activity row (e.g. the
// "Open → Assigned" transition), distinct from the header's Badge pills.
const StatusPill = ({ status }) => {
  const tone = STATUS_TONES[status] || DEFAULT_TONE;
  return (
    <span className="ticket-detail-status-pill" style={{ background: tone.bg, color: tone.color }}>
      <span className="ticket-detail-status-pill-dot" style={{ background: tone.dot }} />
      {status || "-"}
    </span>
  );
};

// The assignee editors use MUI Autocomplete; these overrides retheme its
// default bright-blue "bootstrap" look to the app's brand palette (red accent,
// slate neutrals). Focus mirrors the page's inputs: brand border + soft ring.
// The popup is portaled outside .ticket-detail-page, so colours are literal
// (the --eq-* custom properties wouldn't resolve there).
const assigneeFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "12.5px",
    background: "#fff",
    "& fieldset": { borderColor: "#d9dee7" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px #fde8e7" },
    "&.Mui-focused fieldset": { borderColor: "#e1261c", borderWidth: "1px" },
  },
};

const assigneeSlotProps = {
  paper: {
    sx: {
      mt: 0.5,
      borderRadius: "10px",
      border: "1px solid #e5e8ee",
      boxShadow: "0 10px 28px rgba(16, 24, 40, 0.12)",
      "& .MuiAutocomplete-listbox": { padding: "4px" },
      "& .MuiAutocomplete-option": {
        borderRadius: "7px",
        fontSize: "12.5px",
        color: "#334155",
        minHeight: "auto",
        padding: "5px 8px",
      },
      "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "#f1f5f9" },
      '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: "#fdecec" },
      '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
        backgroundColor: "#fbdcda",
      },
    },
  },
};

const assigneeCheckboxSx = {
  mr: 1,
  p: 0.25,
  color: "#cbd5e1",
  "&.Mui-checked": { color: "#e1261c" },
};

// Mirror the read-only .ticket-detail-assignee-pill: slate text on a light
// surface with a subtle border, so chips match the page's pills exactly.
const assigneeChipSx = {
  height: 22,
  fontSize: "11.5px",
  fontWeight: 600,
  backgroundColor: "#f8fafc",
  color: "#334155",
  border: "1px solid #e5e8ee",
  "& .MuiChip-deleteIcon": { color: "#94a3b8", fontSize: 15 },
  "& .MuiChip-deleteIcon:hover": { color: "#475569" },
};

function TicketDetailV2() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem("user_id") || "";
  const fileInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  // Live-refresh plumbing: the latest ticket (for signature compare) and whether
  // a local mutation/edit is in flight (so a refresh never yanks data mid-edit).
  const ticketRef = useRef(null);
  const mutatingRef = useRef(false);

  // Grow the composer textarea with its content, up to its CSS max-height
  // (after which it scrolls). Keeps multi-line comments fully visible.
  const autoGrowComment = useCallback(() => {
    const el = commentInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");

  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableDepartments, setAssignableDepartments] = useState([]);
  const [canEdit, setCanEdit] = useState(false);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [savingField, setSavingField] = useState(false);

  const [statusSaving, setStatusSaving] = useState(false);

  const [editingAssignees, setEditingAssignees] = useState(false);
  const [draftUsers, setDraftUsers] = useState([]);
  const [draftDepartments, setDraftDepartments] = useState([]);
  const [savingAssignees, setSavingAssignees] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [postingComment, setPostingComment] = useState(false);

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentBody, setEditingCommentBody] = useState("");
  // Mentions parsed from the comment being edited, so tokens survive a re-save.
  const [editMentions, setEditMentions] = useState([]);

  // Comment pending deletion; drives the confirmation dialog (null = closed).
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Direct ticket-attachment upload/delete (separate from comment attachments).
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deleteAttachmentTarget, setDeleteAttachmentTarget] = useState(null);

  // Image lightbox: index into the ticket's image attachments, or null (closed).
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // @-mentions in the composer. `mentions` are the picked {id, name} pairs;
  // `mentionMenu` = { start, query } drives the autocomplete dropdown (null =
  // closed); `mentionActive` is the keyboard-highlighted row.
  const [mentions, setMentions] = useState([]);
  const [mentionMenu, setMentionMenu] = useState(null);
  const [mentionActive, setMentionActive] = useState(0);

  // Optimistic local patch: reflect a change in the UI immediately, before the
  // request round-trips. Every handler still refetches to reconcile with the
  // server (real ids, activity rows, ordering) — and refetches on error, which
  // discards the optimistic edit and restores the true state.
  const patchTicket = useCallback((updater) => {
    setTicket((prev) => (prev ? updater(prev) : prev));
  }, []);

  const fetchTicket = useCallback(() => {
    return axiosInstance
      .get(`${API_URL}/api/ticket/v2/${ticketId}`)
      .then((res) => {
        setTicket(res.data?.data || null);
        setErrorMessage("");
      })
      .catch((error) => {
        setTicket(null);
        setErrorMessage(
          error?.response?.data?.error || "Failed to load this ticket",
        );
      });
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    fetchTicket().finally(() => setLoading(false));
  }, [fetchTicket]);

  // Keep the refs in sync for the live-refresh guards below.
  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);
  useEffect(() => {
    mutatingRef.current =
      postingComment ||
      statusSaving ||
      savingField ||
      savingAssignees ||
      uploadingAttachment ||
      editingTitle ||
      editingDescription ||
      editingAssignees ||
      editingCommentId !== null;
  }, [
    postingComment,
    statusSaving,
    savingField,
    savingAssignees,
    uploadingAttachment,
    editingTitle,
    editingDescription,
    editingAssignees,
    editingCommentId,
  ]);

  // Silent refresh: refetch, but only swap state in when something actually
  // changed (signature compare) — so an unchanged refresh causes no re-render /
  // image reload. Skipped while a local edit/mutation is in flight, so it never
  // pulls data out from under an open editor.
  const refetchIfChanged = useCallback(() => {
    if (mutatingRef.current) return;
    axiosInstance
      .get(`${API_URL}/api/ticket/v2/${ticketId}`)
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;
        if (ticketSignature(data) !== ticketSignature(ticketRef.current)) {
          setTicket(data);
        }
      })
      .catch(() => {}); // silent: a failed background refresh must not disrupt the page
  }, [ticketId]);

  // Live updates for notification recipients: App.js turns a foreground ticket
  // push into a "ticket-update-received" event; refresh if it's for this ticket.
  useEffect(() => {
    const onTicketPush = (e) => {
      if (String(e.detail?.ticket_id) === String(ticketId)) refetchIfChanged();
    };
    window.addEventListener("ticket-update-received", onTicketPush);
    return () => window.removeEventListener("ticket-update-received", onTicketPush);
  }, [ticketId, refetchIfChanged]);

  // Catch up on tab return — covers pushes delivered while the tab was
  // backgrounded (the in-page FCM handler only fires while focused).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refetchIfChanged();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refetchIfChanged]);

  useEffect(() => {
    axiosInstance
      .get(`${API_URL}/access/get_user_name`)
      .then((res) => setCurrentUserName(res.data?.user_name || ""))
      .catch(() => setCurrentUserName(""));
  }, []);

  // Reuse the assignable-users endpoint as an implicit "can edit this ticket"
  // signal: it's gated by the same project-membership check as the PATCH/PUT
  // edit endpoints, so a 200 here means the user can also edit title/
  // description/assignees.
  useEffect(() => {
    if (!ticket?.project_id) return;
    axiosInstance
      .get(`${API_URL}/api/projects/${ticket.project_id}/assignable-users`)
      .then((res) => {
        setAssignableUsers(res.data?.data?.users || []);
        setAssignableDepartments(res.data?.data?.departments || []);
        setCanEdit(true);
      })
      .catch(() => {
        setAssignableUsers([]);
        setAssignableDepartments([]);
        setCanEdit(false);
      });
  }, [ticket?.project_id]);

  // Lightbox keyboard controls: Esc closes, arrows flip through the images.
  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const images = (ticket?.attachments || []).filter((f) => isImageFile(f.filename));
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % images.length);
      else if (e.key === "ArrowLeft")
        setLightboxIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, ticket]);

  const handleBack = () => navigate("/tickets");

  const handleStatusChange = (newStatus) => {
    if (!ticket || newStatus === ticket.status) return;
    const prevStatus = ticket.status;
    setStatusSaving(true);
    patchTicket((prev) => ({
      ...prev,
      status: newStatus,
      activity: [
        ...(prev.activity || []),
        {
          type: "status_change",
          from_status: prevStatus,
          to_status: newStatus,
          actor: { id: currentUserId, name: currentUserName },
          at: new Date().toISOString(),
        },
      ],
    }));
    axiosInstance
      .patch(`${API_URL}/api/ticket/v2/${ticketId}/status`, { status: newStatus })
      .then(() => {
        toast.success("Status updated", toastOpts);
        return fetchTicket();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to update status", toastOpts);
        return fetchTicket();
      })
      .finally(() => setStatusSaving(false));
  };

  const handleSaveTitle = () => {
    const title = titleDraft.trim();
    if (!title) {
      toast.error("Title cannot be empty", toastOpts);
      return;
    }
    setSavingField(true);
    setEditingTitle(false);
    patchTicket((prev) => ({ ...prev, title }));
    axiosInstance
      .patch(`${API_URL}/api/ticket/v2/${ticketId}`, { title })
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to update title", toastOpts);
        return fetchTicket();
      })
      .finally(() => setSavingField(false));
  };

  const handleSaveDescription = () => {
    const description = descriptionDraft.trim();
    setSavingField(true);
    setEditingDescription(false);
    patchTicket((prev) => ({ ...prev, description }));
    axiosInstance
      .patch(`${API_URL}/api/ticket/v2/${ticketId}`, { description })
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to update description", toastOpts);
        return fetchTicket();
      })
      .finally(() => setSavingField(false));
  };

  const openAssigneeEditor = () => {
    setDraftUsers(ticket?.assignees?.users || []);
    // Ensure the mandatory (planning) department(s) are present even if an old
    // ticket predates the always-assign rule — they can't be removed here.
    setDraftDepartments(
      withMandatoryDepartments(
        ticket?.assignees?.departments || [],
        assignableDepartments,
      ),
    );
    setEditingAssignees(true);
  };

  const handleSaveAssignees = () => {
    setSavingAssignees(true);
    setEditingAssignees(false);
    patchTicket((prev) => ({
      ...prev,
      assignees: { users: draftUsers, departments: draftDepartments },
    }));
    axiosInstance
      .put(`${API_URL}/api/ticket/v2/${ticketId}/assignees`, {
        user_ids: draftUsers.map((u) => u.id),
        department_ids: draftDepartments.map((d) => d.id),
      })
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to update assignees", toastOpts);
        return fetchTicket();
      })
      .finally(() => setSavingAssignees(false));
  };

  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setCommentFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  // Upload files straight onto the ticket (not the comment composer). The
  // response carries the real attachment rows (with presigned URLs), so we
  // append them directly instead of refetching the whole ticket.
  const handleUploadAttachments = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("attachments", file));

    setUploadingAttachment(true);
    axiosInstance
      .post(`${API_URL}/api/ticket/v2/${ticketId}/attachments`, formData)
      .then((res) => {
        const added = res.data?.data?.attachments || [];
        patchTicket((prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), ...added],
        }));
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to upload attachment", toastOpts);
      })
      .finally(() => setUploadingAttachment(false));
  };

  // Delete is destructive (removes the file from storage), so confirm first.
  const confirmDeleteAttachment = () => {
    const fileId = deleteAttachmentTarget;
    setDeleteAttachmentTarget(null);
    if (!fileId) return;
    patchTicket((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((f) => f.file_id !== fileId),
    }));
    axiosInstance
      .delete(`${API_URL}/api/ticket/v2/attachments/${fileId}`)
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to delete attachment", toastOpts);
        return fetchTicket();
      });
  };

  // Mentionable users matching the active @query (capped for a tidy menu).
  const getMentionCandidates = (menu) => {
    if (!menu) return [];
    const q = menu.query.toLowerCase();
    return assignableUsers
      .filter((u) => (u.name || "").toLowerCase().includes(q))
      .slice(0, 6);
  };

  // Replace the in-progress "@query" with "@Name " and record the pick.
  const insertMention = (user) => {
    if (!mentionMenu) return;
    const before = commentText.slice(0, mentionMenu.start);
    const after = commentText.slice(mentionMenu.start + 1 + mentionMenu.query.length);
    const insert = `@${user.name} `;
    setCommentText(before + insert + after);
    setMentions((prev) =>
      prev.some((m) => m.id === user.id) ? prev : [...prev, { id: user.id, name: user.name }],
    );
    setMentionMenu(null);
    setMentionActive(0);
    requestAnimationFrame(() => {
      const el = commentInputRef.current;
      if (!el) return;
      const pos = (before + insert).length;
      el.focus();
      el.setSelectionRange(pos, pos);
      autoGrowComment();
    });
  };

  // Turn the composer's "@Name" display text into @[Name](id) tokens for the
  // picked mentions whose text is still present. Longest names first so "@Jane"
  // isn't half-replaced by "@Jan"; ids the backend can't validate are ignored.
  const serializeMentions = (text, list = mentions) => {
    let out = text;
    [...list]
      .sort((a, b) => b.name.length - a.name.length)
      .forEach((m) => {
        out = out.replace(new RegExp(`@${escapeRegExp(m.name)}`, "g"), `@[${m.name}](${m.id})`);
      });
    return out;
  };

  // Inverse of serialize: turn stored @[Name](id) tokens back into "@Name" text
  // for the edit box, and recover the {id, name} list so a re-save re-tokenizes.
  const tokensToDisplay = (body) => {
    const list = [];
    const text = (body || "").replace(MENTION_TOKEN, (_full, name, id) => {
      if (!list.some((m) => m.id === id)) list.push({ id, name });
      return `@${name}`;
    });
    return { text, list };
  };

  const handlePostComment = () => {
    const display = commentText.trim();
    if (!display && commentFiles.length === 0) return;

    // Serialize @Name → @[Name](id) tokens; this is what's stored/notified on.
    const body = serializeMentions(display);

    const formData = new FormData();
    formData.append("body", body);
    commentFiles.forEach((file) => formData.append("attachments", file));

    // Snapshot for rollback, then clear the composer and show the comment now.
    const prevText = commentText;
    const prevFiles = commentFiles;
    const prevMentions = mentions;
    const tempId = `temp-${new Date().getTime()}`;
    setCommentText("");
    setCommentFiles([]);
    setMentions([]);
    setMentionMenu(null);
    if (commentInputRef.current) commentInputRef.current.style.height = "auto";
    patchTicket((prev) => ({
      ...prev,
      activity: [
        ...(prev.activity || []),
        {
          comment_id: tempId,
          body,
          actor: { id: currentUserId, name: currentUserName },
          at: new Date().toISOString(),
          is_edited: false,
          // Files are still uploading; the refetch on success fills these in.
          attachments: [],
        },
      ],
    }));

    setPostingComment(true);
    axiosInstance
      .post(`${API_URL}/api/ticket/v2/${ticketId}/comments`, formData)
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to post comment", toastOpts);
        setCommentText(prevText);
        setCommentFiles(prevFiles);
        setMentions(prevMentions);
        return fetchTicket();
      })
      .finally(() => setPostingComment(false));
  };

  const handleCommentKeyDown = (e) => {
    // While the @-menu is open, arrows/enter/tab/esc drive it — not the composer.
    const candidates = getMentionCandidates(mentionMenu);
    if (mentionMenu && candidates.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionActive((i) => (i + 1) % candidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionActive((i) => (i - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(candidates[Math.min(mentionActive, candidates.length - 1)]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionMenu(null);
        return;
      }
    }
    // Enter sends; Shift+Enter (or ⌘/Ctrl+Enter) inserts a newline.
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      handlePostComment();
    }
  };

  // Render @[Name](id) tokens as highlighted chips for known (mentionable)
  // users, or plain "@Name" text otherwise, so a mention still reads sensibly
  // even if that person is no longer in the list.
  const renderCommentBody = (body) => {
    if (!body) return body;
    const known = new Set(assignableUsers.map((u) => u.id));
    const nodes = [];
    let last = 0;
    let key = 0;
    for (const m of body.matchAll(MENTION_TOKEN)) {
      if (m.index > last) nodes.push(body.slice(last, m.index));
      const name = m[1];
      const id = m[2];
      nodes.push(
        known.has(id) ? (
          <span key={`mention-${key++}`} className="ticket-detail-mention">
            @{name}
          </span>
        ) : (
          `@${name}`
        ),
      );
      last = m.index + m[0].length;
    }
    if (last < body.length) nodes.push(body.slice(last));
    return nodes.length ? nodes : body;
  };

  const startEditComment = (item) => {
    const { text, list } = tokensToDisplay(item.body);
    setEditingCommentId(item.comment_id);
    setEditingCommentBody(text);
    setEditMentions(list);
  };

  const handleSaveCommentEdit = () => {
    const display = editingCommentBody.trim();
    if (!display) return;
    // Re-tokenize the still-present @Name mentions so they survive the edit.
    const body = serializeMentions(display, editMentions);
    const commentId = editingCommentId;
    setEditingCommentId(null);
    patchTicket((prev) => ({
      ...prev,
      activity: (prev.activity || []).map((it) =>
        it.comment_id === commentId ? { ...it, body, is_edited: true } : it,
      ),
    }));
    axiosInstance
      .patch(`${API_URL}/api/ticket/v2/comments/${commentId}`, { body })
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to edit comment", toastOpts);
        return fetchTicket();
      });
  };

  // Delete is destructive, so route through a confirmation dialog.
  const confirmDeleteComment = () => {
    const commentId = deleteTarget;
    setDeleteTarget(null);
    if (!commentId) return;
    patchTicket((prev) => ({
      ...prev,
      activity: (prev.activity || []).filter((it) => it.comment_id !== commentId),
    }));
    axiosInstance
      .delete(`${API_URL}/api/ticket/v2/comments/${commentId}`)
      .then(() => fetchTicket())
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Failed to delete comment", toastOpts);
        return fetchTicket();
      });
  };

  const handleEntityClick = () => {
    if (!ticket) return;

    // The flanges / equipments pages are scoped to the globally selected
    // project. A ticket can belong to a different project than the one
    // currently selected, in which case the target entity isn't in the list
    // and the page reports it as "not found". Switch to the ticket's project
    // first and broadcast the change so the sidebar and other project-scoped
    // views stay in sync.
    if (ticket.project_id) {
      let storedProject = null;
      try {
        storedProject = JSON.parse(localStorage.getItem("selectedProject"));
      } catch {
        storedProject = null;
      }
      const currentProjectId = String(storedProject?.id || storedProject || "");
      if (currentProjectId !== String(ticket.project_id)) {
        localStorage.setItem("selectedProject", JSON.stringify(ticket.project_id));
        window.dispatchEvent(
          new CustomEvent("projectChanged", {
            detail: { project_id: ticket.project_id, type: "selected" },
          }),
        );
      }
    }

    if (ticket.entity_type === "flange" && ticket.flange_id) {
      navigate(`/flanges?scrollTo=${ticket.flange_id}&flangeNumber=${ticket.flange_number || ""}`);
    } else if (ticket.entity_type === "equipment" && ticket.equipment_id) {
      navigate(`/equipments?highlightEquipmentId=${ticket.equipment_id}`);
    }
  };

  if (loading) {
    return (
      <div className="table-container ticket-detail-page expanded">
        <div className="ticket-detail-loading">
          <CircularProgress size={22} />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="table-container ticket-detail-page expanded">
        <div className="ticket-detail-error">{errorMessage || "Ticket not found"}</div>
      </div>
    );
  }

  const statusTone = STATUS_TONES[ticket.status] || DEFAULT_TONE;
  const entityNumber =
    ticket.entity_type === "flange" ? ticket.flange_number : ticket.equipment_number;

  const attachments = ticket.attachments || [];
  const imageAttachments = attachments.filter((f) => isImageFile(f.filename));
  const docAttachments = attachments.filter((f) => !isImageFile(f.filename));
  const lightboxImage =
    lightboxIndex !== null ? imageAttachments[lightboxIndex] || null : null;

  const assigneeUsers = uniqueById(ticket.assignees?.users);
  const assigneeDepartments = uniqueById(ticket.assignees?.departments);
  const hasAssignees = assigneeUsers.length > 0 || assigneeDepartments.length > 0;

  return (
    <div className="table-container ticket-detail-page expanded">
      <ToastContainer containerId={TOAST_CONTAINER_ID} position="top-right" autoClose={4000} />

      <div className="ticket-detail-content">
        <button type="button" className="ticket-detail-back" onClick={handleBack}>
          <ArrowBackIcon sx={{ fontSize: 15 }} />
          Tickets
        </button>

        {/* Header: title first, then ticket context and primary actions. */}
        <div className="ticket-detail-header">
          <div className="ticket-detail-title-row">
            {editingTitle ? (
              <div style={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value.slice(0, 255))}
                  autoFocus
                />
                <div className="ticket-detail-edit-actions">
                  <button type="button" className="ticket-detail-btn ticket-detail-btn-primary" disabled={savingField} onClick={handleSaveTitle}>Save</button>
                  <button type="button" className="ticket-detail-btn" onClick={() => setEditingTitle(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <span className="ticket-detail-title">{ticket.title}</span>
                {canEdit && (
                  <button type="button" className="ticket-detail-title-edit" aria-label="Edit title" onClick={() => { setTitleDraft(ticket.title); setEditingTitle(true); }}>
                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                  </button>
                )}
              </>
            )}
          </div>

          <div className="ticket-detail-header-top">
            <div className="ticket-detail-header-meta">
              <Badge mono label={ticket.ticket_number} />
              <Badge
                tone={TYPE_TONES[ticket.ticket_type]}
                icon={TYPE_ICONS[ticket.ticket_type]}
                label={ticket.ticket_type === "Missing Information" ? "Missing Info" : ticket.ticket_type}
              />
              <Badge tone={PRIORITY_TONES[ticket.priority]} label={ticket.priority} />
            </div>

            <div className="ticket-detail-header-actions">
              {canEdit && (
                <button type="button" className="ticket-detail-header-btn" onClick={() => { setTitleDraft(ticket.title); setEditingTitle(true); }}>
                  <EditOutlinedIcon sx={{ fontSize: 16 }} /> Edit
                </button>
              )}
              <span className="ticket-detail-action-select">
                <AutorenewIcon sx={{ fontSize: 16 }} />
              <Select
                size="small"
                value={ticket.status}
                disabled={statusSaving}
                onChange={(e) => handleStatusChange(e.target.value)}
                renderValue={(v) => <StatusLabel status={v} />}
                sx={{
                  minWidth: 130,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: statusTone.color,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--eq-border-strong)" },
                  "& .MuiSelect-select": { py: 0.5 },
                }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    <StatusLabel status={s} />
                  </MenuItem>
                ))}
              </Select>
              </span>
              {canEdit && (
                <button type="button" className="ticket-detail-header-btn ticket-detail-header-btn-accent" onClick={openAssigneeEditor}>
                  <PersonAddAlt1Icon sx={{ fontSize: 16 }} /> Assign
                </button>
              )}
            </div>
          </div>
          {ticket.status !== "Closed" && (
            <span className="ticket-detail-status-hint">Only Planning can close this ticket</span>
          )}
        </div>

        <div className="ticket-detail-layout">
          {/* Main column */}
          <div className="ticket-detail-main">
            <div className="ticket-detail-scrollable-content">
              <div className="ticket-detail-section ticket-detail-section-first">
              <div className="ticket-detail-section-header">
                <span className="ticket-detail-section-label">Description</span>
                {!editingDescription && canEdit && (
                  <button
                    type="button"
                    className="ticket-detail-text-action"
                    onClick={() => {
                      setDescriptionDraft(ticket.description || "");
                      setEditingDescription(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {editingDescription ? (
                <div>
                  <textarea
                    className="ticket-detail-edit-input"
                    rows={4}
                    value={descriptionDraft}
                    onChange={(e) => setDescriptionDraft(e.target.value)}
                    autoFocus
                  />
                  <div className="ticket-detail-edit-actions">
                    <button
                      type="button"
                      className="ticket-detail-btn ticket-detail-btn-primary"
                      disabled={savingField}
                      onClick={handleSaveDescription}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="ticket-detail-btn"
                      onClick={() => setEditingDescription(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ticket-detail-description">
                  {ticket.description || "No description provided."}
                </div>
              )}
            </div>

              <div className="ticket-detail-section">
              <div className="ticket-detail-section-header">
                <span className="ticket-detail-section-label ticket-detail-section-label-icon">
                  <AttachFileIcon sx={{ fontSize: 13 }} />
                  Attachments ({attachments.length})
                </span>
              </div>

              {imageAttachments.length > 0 && (
                <div className="ticket-detail-thumb-grid">
                  {imageAttachments.map((file, idx) => (
                    <div key={file.file_id} className="ticket-detail-attachment-wrap">
                      <button
                        type="button"
                        className="ticket-detail-thumb"
                        onClick={() => setLightboxIndex(idx)}
                      >
                        <img src={file.url} alt={file.filename} />
                        <span className="ticket-detail-thumb-label">
                          <ImageOutlinedIcon sx={{ fontSize: 12 }} />
                          <span className="ticket-detail-thumb-name">{file.filename}</span>
                        </span>
                      </button>
                      <div className="ticket-detail-attachment-actions">
                        <a
                          href={file.download_url || file.url}
                          className="ticket-detail-attachment-action"
                          aria-label="Download attachment"
                          title="Download"
                          download
                        >
                          <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
                        </a>
                        {canEdit && !file.comment_id && (
                          <button
                            type="button"
                            className="ticket-detail-attachment-action ticket-detail-attachment-action-danger"
                            aria-label="Delete attachment"
                            title="Delete"
                            onClick={() => setDeleteAttachmentTarget(file.file_id)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {docAttachments.length > 0 && (
                <div className="ticket-detail-file-grid">
                  {docAttachments.map((file) => (
                    <div key={file.file_id} className="ticket-detail-attachment-wrap">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="ticket-detail-file-card"
                      >
                        <span className="ticket-detail-file-icon">
                          <InsertDriveFileIcon sx={{ fontSize: 20 }} />
                        </span>
                        <div className="ticket-detail-file-meta">
                          <div className="ticket-detail-file-name">{file.filename}</div>
                          <div className="ticket-detail-file-sub">{fileMetaLabel(file)}</div>
                        </div>
                      </a>
                      <div className="ticket-detail-attachment-actions">
                        <a
                          href={file.download_url || file.url}
                          className="ticket-detail-attachment-action"
                          aria-label="Download attachment"
                          title="Download"
                          download
                        >
                          <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
                        </a>
                        {canEdit && !file.comment_id && (
                          <button
                            type="button"
                            className="ticket-detail-attachment-action ticket-detail-attachment-action-danger"
                            aria-label="Delete attachment"
                            title="Delete"
                            onClick={() => setDeleteAttachmentTarget(file.file_id)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {attachments.length === 0 && (
                <div className="ticket-detail-empty-note">No attachments yet</div>
              )}

              <button
                type="button"
                className="ticket-detail-upload-btn"
                disabled={uploadingAttachment}
                onClick={() => attachmentInputRef.current?.click()}
              >
                {uploadingAttachment ? (
                  <CircularProgress size={13} sx={{ color: "inherit" }} />
                ) : (
                  <UploadFileOutlinedIcon sx={{ fontSize: 15 }} />
                )}
                {uploadingAttachment ? "Uploading..." : "Upload attachment"}
              </button>
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                hidden
                accept="image/jpeg,image/png,image/jpg,video/mp4,video/quicktime,application/pdf"
                onChange={handleUploadAttachments}
              />
            </div>

              <div className="ticket-detail-section">
              <div className="ticket-detail-section-header">
                <span className="ticket-detail-section-label">Activity</span>
                <span className="ticket-detail-section-note">
                  Times shown in your local time
                </span>
              </div>
              <div className="ticket-detail-timeline">
                {(ticket.activity || []).map((item, index) => {
                  const isLast = index === (ticket.activity || []).length - 1;
                  const rowClass = `ticket-detail-timeline-row${isLast ? " is-last" : ""}`;

                  if (item.type === "status_change" && !item.from_status) {
                    // Genesis event: the ticket's creation.
                    return (
                      <div key={`status-${index}`} className={rowClass}>
                        <span className="ticket-detail-timeline-icon ticket-detail-timeline-icon-create">
                          <AddIcon sx={{ fontSize: 16 }} />
                        </span>
                        <div className="ticket-detail-timeline-content">
                          <div className="ticket-detail-timeline-line1">
                            <strong>{item.actor?.name || "Someone"}</strong> raised this ticket
                            <span className="ticket-detail-timeline-time">
                              {formatDateTime(item.at)}
                            </span>
                          </div>
                          {entityNumber && (
                            <div className="ticket-detail-timeline-subtext">
                              "Raised from {ticket.entity_type} {entityNumber}"
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (item.type === "status_change") {
                    return (
                      <div key={`status-${index}`} className={rowClass}>
                        <span className="ticket-detail-timeline-icon ticket-detail-timeline-icon-status">
                          <AutorenewIcon sx={{ fontSize: 15 }} />
                        </span>
                        <div className="ticket-detail-timeline-content">
                          <div className="ticket-detail-timeline-line1">
                            <strong>{item.actor?.name || "Someone"}</strong> moved status
                            <StatusPill status={item.from_status} />
                            <span className="ticket-detail-timeline-arrow">→</span>
                            <StatusPill status={item.to_status} />
                            <span className="ticket-detail-timeline-time">
                              {formatDateTime(item.at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (item.type === "assignment") {
                    return (
                      <div key={`assign-${index}`} className={rowClass}>
                        <span className="ticket-detail-timeline-icon ticket-detail-timeline-icon-assign">
                          <PersonAddAlt1Icon sx={{ fontSize: 14 }} />
                        </span>
                        <div className="ticket-detail-timeline-content">
                          <div className="ticket-detail-timeline-line1">
                            <strong>{item.actor?.name || "Someone"}</strong>{" "}
                            {item.action === "unassigned" ? "unassigned" : "assigned"}{" "}
                            <strong>{item.target?.name || "someone"}</strong>
                            <span className="ticket-detail-timeline-time">
                              {formatDateTime(item.at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  // comment
                  const isMine = item.actor?.id === currentUserId;
                  const isEditingThis = editingCommentId === item.comment_id;
                  return (
                    <div key={item.comment_id} className={rowClass}>
                      <span
                        className="ticket-detail-timeline-icon"
                        style={{ background: colorForName(item.actor?.name) }}
                      >
                        {initialsOf(item.actor?.name)}
                      </span>
                      <div className="ticket-detail-timeline-content">
                        <div className="ticket-detail-comment-card">
                          <div className="ticket-detail-comment-header">
                            <span className="ticket-detail-comment-author">{item.actor?.name}</span>
                            <span className="ticket-detail-comment-time">
                              {formatDateTime(item.at)}
                              {item.is_edited ? " (edited)" : ""}
                            </span>
                            {isMine && !isEditingThis && (
                              <div className="ticket-detail-comment-actions">
                                <button
                                  type="button"
                                  className="ticket-detail-comment-action"
                                  aria-label="Edit comment"
                                  title="Edit"
                                  onClick={() => startEditComment(item)}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                </button>
                                <button
                                  type="button"
                                  className="ticket-detail-comment-action ticket-detail-comment-action-danger"
                                  aria-label="Delete comment"
                                  title="Delete"
                                  onClick={() => setDeleteTarget(item.comment_id)}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </button>
                              </div>
                            )}
                          </div>
                          {isEditingThis ? (
                            <div>
                              <textarea
                                className="ticket-detail-edit-input"
                                rows={2}
                                value={editingCommentBody}
                                onChange={(e) => setEditingCommentBody(e.target.value)}
                                autoFocus
                              />
                              <div className="ticket-detail-edit-actions">
                                <button
                                  type="button"
                                  className="ticket-detail-btn ticket-detail-btn-primary"
                                  onClick={handleSaveCommentEdit}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="ticket-detail-btn"
                                  onClick={() => setEditingCommentId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="ticket-detail-comment-body">
                                {renderCommentBody(item.body)}
                              </div>
                              {item.attachments?.length > 0 && (
                                <div className="ticket-detail-comment-attachments">
                                  {item.attachments.map((file) =>
                                    isImageFile(file.filename) ? (
                                      <a
                                        key={file.file_id}
                                        href={file.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ticket-detail-comment-attachment ticket-detail-comment-attachment-image"
                                        title={file.filename}
                                      >
                                        <img src={file.url} alt={file.filename} />
                                      </a>
                                    ) : (
                                      <a
                                        key={file.file_id}
                                        href={file.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ticket-detail-comment-attachment"
                                        title={file.filename}
                                      >
                                        <InsertDriveFileIcon sx={{ fontSize: 16 }} />
                                        <span className="ticket-detail-comment-attachment-name">
                                          {file.filename}
                                        </span>
                                      </a>
                                    )
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>

            {/* Comment composer */}
            <div className="ticket-detail-composer">
              <span
                className="ticket-detail-avatar"
                style={{ background: colorForName(currentUserName) }}
              >
                {initialsOf(currentUserName)}
              </span>
              <div className="ticket-detail-composer-box">
                {commentFiles.length > 0 && (
                  <div className="ticket-detail-composer-pending">
                    {commentFiles.map((file, index) => (
                      <span key={`${file.name}-${index}`} className="ticket-detail-composer-pending-chip">
                        {file.name}
                        <CloseIcon
                          sx={{ fontSize: 13, cursor: "pointer" }}
                          onClick={() =>
                            setCommentFiles((prev) => prev.filter((_, i) => i !== index))
                          }
                        />
                      </span>
                    ))}
                  </div>
                )}
                {mentionMenu && getMentionCandidates(mentionMenu).length > 0 && (
                  <div className="ticket-detail-mention-menu">
                    {getMentionCandidates(mentionMenu).map((user, i) => (
                      <button
                        type="button"
                        key={user.id}
                        className={`ticket-detail-mention-option${
                          i === mentionActive ? " active" : ""
                        }`}
                        // onMouseDown (not onClick) so the textarea doesn't blur first.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertMention(user);
                        }}
                        onMouseEnter={() => setMentionActive(i)}
                      >
                        <span
                          className="ticket-detail-mention-avatar"
                          style={{ background: colorForName(user.name) }}
                        >
                          {initialsOf(user.name)}
                        </span>
                        {user.name}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  ref={commentInputRef}
                  className="ticket-detail-composer-input"
                  rows={1}
                  placeholder="Add a comment...  (@ to mention, Enter to send)"
                  value={commentText}
                  onChange={(e) => {
                    setCommentText(e.target.value);
                    autoGrowComment();
                    setMentionMenu(detectMentionTrigger(e.target.value, e.target.selectionStart));
                    setMentionActive(0);
                  }}
                  onKeyDown={handleCommentKeyDown}
                  onBlur={() => setMentionMenu(null)}
                />
                <div className="ticket-detail-composer-footer">
                  <button
                    type="button"
                    className="ticket-detail-composer-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachFileIcon sx={{ fontSize: 16 }} />
                    Attach
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept="image/jpeg,image/png,image/jpg,video/mp4,video/quicktime,application/pdf"
                    onChange={handlePickFiles}
                  />
                  <button
                    type="button"
                    className="ticket-detail-composer-send"
                    disabled={postingComment || (!commentText.trim() && commentFiles.length === 0)}
                    onClick={handlePostComment}
                  >
                    <SendIcon sx={{ fontSize: 14 }} />
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="ticket-detail-sidebar">
            <div className="ticket-detail-sidebar-card">
            <div className="ticket-detail-sidebar-section">
              <div className="ticket-detail-sidebar-header">
                <div className="ticket-detail-sidebar-title">Assignees</div>
                {!editingAssignees && canEdit && hasAssignees && (
                  <button
                    type="button"
                    className="ticket-detail-text-action"
                    onClick={openAssigneeEditor}
                  >
                    Edit
                  </button>
                )}
              </div>
              {editingAssignees ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    sx={assigneeFieldSx}
                    slotProps={assigneeSlotProps}
                    options={assignableUsers}
                    value={draftUsers}
                    onChange={(e, value) => setDraftUsers(value)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={selected}
                          size="small"
                          sx={assigneeCheckboxSx}
                        />
                        {option.name}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" sx={assigneeChipSx} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} placeholder="Add people..." />}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    sx={assigneeFieldSx}
                    slotProps={assigneeSlotProps}
                    options={assignableDepartments}
                    value={draftDepartments}
                    onChange={(e, value) =>
                      setDraftDepartments(
                        withMandatoryDepartments(value, assignableDepartments),
                      )
                    }
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionDisabled={(option) => Boolean(option.mandatory)}
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={selected}
                          size="small"
                          sx={assigneeCheckboxSx}
                        />
                        {option.name}
                        {option.mandatory && (
                          <span style={{ marginLeft: "auto", fontSize: "0.7rem", color: "#991b1b" }}>
                            Always assigned
                          </span>
                        )}
                      </li>
                    )}
                    renderTags={(value, getTagProps) => {
                      // draftDepartments come from the ticket's saved assignees
                      // and may lack the `mandatory` flag, so resolve it from the
                      // assignable list by id.
                      const mandatoryIds = mandatoryDeptIdSet(assignableDepartments);
                      return value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        if (mandatoryIds.has(option.id)) {
                          // Locked chip: onDelete overridden to undefined (backend
                          // also refuses to unassign the planning team).
                          return (
                            <Chip
                              {...tagProps}
                              onDelete={undefined}
                              key={option.id}
                              label={option.name}
                              size="small"
                              sx={assigneeChipSx}
                              icon={<LockIcon sx={{ fontSize: "0.8rem" }} />}
                              title="Planning is always assigned and can't be removed"
                            />
                          );
                        }
                        return (
                          <Chip {...tagProps} key={option.id} label={option.name} size="small" sx={assigneeChipSx} />
                        );
                      });
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Add departments..." />}
                  />
                  <div className="ticket-detail-edit-actions">
                    <button
                      type="button"
                      className="ticket-detail-btn ticket-detail-btn-primary"
                      disabled={savingAssignees}
                      onClick={handleSaveAssignees}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="ticket-detail-btn"
                      onClick={() => setEditingAssignees(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ticket-detail-assignee-list">
                  {assigneeUsers.map((u) => (
                    <span key={u.id} className="ticket-detail-assignee-pill ticket-detail-assignee-pill-person">
                      <span className="ticket-detail-assignee-avatar">{initialsOf(u.name)}</span>
                      {u.name}
                    </span>
                  ))}
                  {assigneeDepartments.map((d) => (
                    <span key={d.id} className="ticket-detail-assignee-pill ticket-detail-assignee-pill-dept">
                      <span className="ticket-detail-assignee-dot" />
                      {d.name}
                    </span>
                  ))}
                  {!hasAssignees && (
                    <span className="ticket-detail-unassigned">Unassigned</span>
                  )}
                  {canEdit && !hasAssignees && (
                    <button
                      type="button"
                      className="ticket-detail-assignee-add-circle"
                      aria-label="Add assignee"
                      onClick={openAssigneeEditor}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="ticket-detail-sidebar-section">
              <div className="ticket-detail-sidebar-title">Details</div>

              <div className="ticket-detail-detail-row">
                <span className="ticket-detail-detail-label">Reporter</span>
                <span className="ticket-detail-detail-value">
                  <span
                    className="ticket-detail-avatar"
                    style={{ width: 22, height: 22, fontSize: 9.5, background: colorForName(ticket.raised_by_name) }}
                  >
                    {initialsOf(ticket.raised_by_name)}
                  </span>
                  {ticket.raised_by_name || "-"}
                </span>
              </div>

              <div className="ticket-detail-detail-row">
                <span className="ticket-detail-detail-label">Project</span>
                <span className="ticket-detail-detail-value">{ticket.project_name || "-"}</span>
              </div>

              <div className="ticket-detail-detail-row">
                <span className="ticket-detail-detail-label">
                  {ticket.entity_type === "flange" ? "Entity · Flange" : "Entity · Equipment"}
                </span>
                <span className="ticket-detail-detail-value">
                  <span className="ticket-detail-entity-value">
                    <span className="ticket-detail-entity-pill" onClick={handleEntityClick}>
                      {entityNumber || "-"}
                    </span>
                    {ticket.entity_type === "flange" && ticket.equipment_number && (
                      <span className="ticket-detail-entity-secondary">
                        <BuildOutlinedIcon sx={{ fontSize: 12 }} />
                        {ticket.equipment_number}
                      </span>
                    )}
                  </span>
                </span>
              </div>

              <div className="ticket-detail-detail-row">
                <span className="ticket-detail-detail-label">Created</span>
                <span className="ticket-detail-detail-value">
                  {formatRelativeAbsolute(ticket.created_at)}
                </span>
              </div>

              <div className="ticket-detail-detail-row">
                <span className="ticket-detail-detail-label">Last updated</span>
                <span className="ticket-detail-detail-value">
                  {formatRelativeTime(ticket.updated_at)}
                </span>
              </div>
            </div>
            </div>

            <div className="ticket-detail-sidebar-card ticket-detail-state-card">
              <div className="ticket-detail-sidebar-title">Status &amp; Priority</div>
              <div className="ticket-detail-state-grid">
                <div>
                  <span className="ticket-detail-state-label">Status</span>
                  <span className="ticket-detail-state-pill" style={{ color: statusTone.color, background: statusTone.bg }}>
                    <span className="ticket-detail-badge-dot" style={{ background: statusTone.dot }} />
                    {ticket.status}
                  </span>
                </div>
                <div>
                  <span className="ticket-detail-state-label">Priority</span>
                  <span className="ticket-detail-state-pill" style={{ color: (PRIORITY_TONES[ticket.priority] || DEFAULT_TONE).color, background: (PRIORITY_TONES[ticket.priority] || DEFAULT_TONE).bg }}>
                    <span className="ticket-detail-badge-dot" style={{ background: (PRIORITY_TONES[ticket.priority] || DEFAULT_TONE).dot }} />
                    {ticket.priority}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 1 }}>
          Delete comment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.85rem", color: "#475569" }}>
            This comment will be permanently removed. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <button
            type="button"
            className="ticket-detail-btn"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ticket-detail-btn ticket-detail-btn-danger"
            onClick={confirmDeleteComment}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteAttachmentTarget}
        onClose={() => setDeleteAttachmentTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 1 }}>
          Delete attachment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.85rem", color: "#475569" }}>
            This file will be permanently removed. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <button
            type="button"
            className="ticket-detail-btn"
            onClick={() => setDeleteAttachmentTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ticket-detail-btn ticket-detail-btn-danger"
            onClick={confirmDeleteAttachment}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>

      {lightboxImage && (
        <div
          className="ticket-detail-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="ticket-detail-lightbox-close"
            aria-label="Close"
            onClick={() => setLightboxIndex(null)}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>

          {imageAttachments.length > 1 && (
            <button
              type="button"
              className="ticket-detail-lightbox-nav ticket-detail-lightbox-prev"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(
                  (i) => (i - 1 + imageAttachments.length) % imageAttachments.length,
                );
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 30 }} />
            </button>
          )}

          <div
            className="ticket-detail-lightbox-stage"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.url}
              alt={lightboxImage.filename}
              className="ticket-detail-lightbox-img"
            />
            <div className="ticket-detail-lightbox-bar">
              <span className="ticket-detail-lightbox-name">{lightboxImage.filename}</span>
              <span className="ticket-detail-lightbox-count">
                {lightboxIndex + 1} / {imageAttachments.length}
              </span>
              <a
                href={lightboxImage.download_url || lightboxImage.url}
                className="ticket-detail-lightbox-download"
                download
                onClick={(e) => e.stopPropagation()}
              >
                <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Download
              </a>
            </div>
          </div>

          {imageAttachments.length > 1 && (
            <button
              type="button"
              className="ticket-detail-lightbox-nav ticket-detail-lightbox-next"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) => (i + 1) % imageAttachments.length);
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 30 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TicketDetailV2;
